import { useContext, useState } from "react";
import styles from "./Bxgy.module.css";
import {
    Button,
    Checkbox,
    Collapsible,
    Divider,
    FormLayout,
    Icon,
    InlineError,
    Label,
    RadioButton,
    Text,
    Tooltip,
} from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { BundleTypes } from "../constants";
import { asChoiceField, useField } from "@shopify/react-form";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import RenderSelectedProduct from "../../../components/RenderSelectedProduct/RenderSelectedProduct";
import { BxgyBundleContext } from "./Bxgy";
import BundleInfo from "../CommonFields/BundleInfo";
import Discount from "../CommonFields/Discount";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import AllowedProductPages from "../../../components/AllowedProductPages/AllowedProductPages";
import FreeGift from "../../../components/FreeGift/FreeGift";
import TextFields from "../CommonFields/TextFields";
import SwatchesSelectorWrapper from "../CommonFields/SwatchesSelectorWrapper";
import ComboProductPage from "../../../components/ComboProductPage/ComboProductPage";
import { ProductObj } from "../Types";

const BxgyContentForm = ({ showBundlerLevel = false }: { showBundlerLevel: boolean }) => {
    const [expandProductSetup, setExpandProductSetup] = useState(true);
    const { trackFormSections } = useBundleMixpanelHook();
    const context = useContext(BxgyBundleContext);

    const {
        fields: {
            bundle_name,
            title,
            description,
            bundle_product_level,
            products,
            discounted_products,
            discount_type,
            discount_value,
            order_discounts,
            product_discounts,
            shipping_discounts,
            add_to_cart_cta_text,
            isProductPageVisible,
            bundle_as_product: { product_title, product_description, images, double_discount, collections },
            quantity_text,
            footer_total_text,
            free_text,
            y_label_get_text,
            y_label_off_text,
            x_label_buy_text,
            allowed_product_pages,
            render_product_pages_type,
            show_swatches,
            swatchOptions,
            swatchesProductsMap,
            swatchShape,
            swatchSize,
            bundles_design: { default_cta },
            allow_free_gifts,
            free_gifts,
            free_gift_text,
            show_product_quantity,
            discounted_variant_mode,
            offer_name,
            discount_label_template,
        },
        handleNext = () => {},
        dispatchBundleConfig,
        submit,
        bundleConfig,
        isMobile,
        SwatchesBundleConfigTouchedRef,
    } = context;

    const combinedProducts = {
        value: [...products.value, ...discounted_products.value],
        defaultValue: [...products.defaultValue, ...discounted_products.defaultValue],
        touched: products.touched || discounted_products.touched,
        dirty: products.dirty || discounted_products.dirty,
        error: products.error || discounted_products.error,
        allErrors: [],
        onBlur: () => {},
        onChange: () => {},
        runValidation: products.runValidation,
        setError: () => {},
        newDefaultValue: () => {},
        reset: () => {},
    };

    typeof combinedProducts;

    return (
        <div className={styles.formWrapper}>
            <div style={{ marginBottom: "4px" }}>
                <Divider borderColor="border" />
            </div>
            <div className={styles.collapsibleFormBox}>
                <BundleInfo
                    description={description}
                    title={title}
                    bundleType={"bxgy"}
                    bundle_name={bundle_name}
                    dispatchBundleConfig={dispatchBundleConfig}
                    offer_name={offer_name}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <div
                    className={styles.sectionTitleWrapper}
                    onClick={() =>
                        setExpandProductSetup((ps) => {
                            trackFormSections({
                                cta: "Form Section",
                                ctaType: `Product Setup ${ps ? "Collapsed" : "Expanded"}`,
                                bundleType: "bxgy",
                            });
                            return !ps;
                        })
                    }
                >
                    <div>Product Setup</div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandProductSetup ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} />
                    </div>
                </div>
                <Collapsible
                    open={expandProductSetup}
                    id="basic-collapsible"
                    transition={{
                        duration: "500ms",
                        timingFunction: "ease-in-out",
                    }}
                    expandOnPrint
                >
                    <FormLayout>
                        {showBundlerLevel && (
                            <div className={styles.customLabelWrapper}>
                                <div className={styles.customLabelWrapper} style={{ marginBottom: "5px" }}>
                                    <Label id="bundle_product_level">Bundle Level</Label>
                                </div>
                            </div>
                        )}

                        {showBundlerLevel && (
                            <div className={`${styles.formCol}`}>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        marginTop: "-5px",
                                    }}
                                >
                                    <RadioButton
                                        label={"Product Level: Variants in Dropdown"}
                                        id="product"
                                        helpText="Selected variants will appear in a dropdown under each product in the bundle."
                                        value="product"
                                        name="bundle_product_level"
                                        {...asChoiceField(bundle_product_level, "product")}
                                        onChange={(status, val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "bundle_product_level",
                                            });
                                            dispatchBundleConfig({
                                                payload: [],
                                                key: "products",
                                            });
                                            dispatchBundleConfig({
                                                payload: [],
                                                key: "discounted_products",
                                            });

                                            products.onChange([]);
                                            discounted_products.onChange([]);
                                            bundle_product_level.onChange(val);
                                        }}
                                    />
                                    {bundle_product_level.value == "variant" && (
                                        <RadioButton
                                            label={"Variant Level: Variants as Standalone"}
                                            id="variant"
                                            helpText="Selected variants will be displayed as individual, standalone products in the bundle."
                                            value="variant"
                                            name="bundle_product_level"
                                            {...asChoiceField(bundle_product_level, "variant")}
                                            onChange={(status, val) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "bundle_product_level",
                                                });
                                                dispatchBundleConfig({
                                                    payload: [],
                                                    key: "products",
                                                });
                                                dispatchBundleConfig({
                                                    payload: [],
                                                    key: "discounted_products",
                                                });
                                                products.onChange([]);
                                                discounted_products.onChange([]);
                                                bundle_product_level.onChange(val);
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                        <div>
                            <RenderSelectedProduct
                                submit={submit}
                                key={"products"}
                                updateKey={"products"}
                                bundleLevel={bundle_product_level.value}
                                dispatchBundleConfig={dispatchBundleConfig}
                                bundleType={BundleTypes.BXGY}
                                products={products}
                                isMobile={isMobile}
                                multiple={true}
                                showVariantPicker={true}
                            />
                            <InlineError message={products.error} fieldID="products" />
                        </div>
                        <Divider />
                        <div>
                            <RenderSelectedProduct
                                submit={submit}
                                key={"discounted_products"}
                                updateKey={"discounted_products"}
                                bundleLevel={bundle_product_level.value}
                                dispatchBundleConfig={dispatchBundleConfig}
                                bundleType={BundleTypes.BXGY}
                                products={discounted_products}
                                customTitle={"Discounted"}
                                isMobile={isMobile}
                                multiple={true}
                                showVariantPicker={true}
                            />
                            <InlineError message={discounted_products.error} fieldID="discounted_products" />
                        </div>
                        <Divider />
                        <div>
                            <div className={styles.customLabelWrapper} style={{ marginBottom: "5px" }}>
                                <Label id="discounted_variant_mode">Discounted item variant</Label>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                {[
                                    {
                                        value: "customer_chooses",
                                        label: "Customer chooses",
                                        helpText: "Customer picks any eligible variant of each discounted product.",
                                    },
                                    {
                                        value: "same_as_buy",
                                        label: "Same variant as Buy item",
                                        helpText:
                                            "If a discounted product is also in the Buy list, the discounted unit is the exact variant the customer picked there. Other discounted products: customer chooses.",
                                        note: "All varaints that are present in X, should be present in Y product selection of the discounted product for this option to work",
                                    },
                                ].map((option) => (
                                    <RadioButton
                                        key={option.value}
                                        label={
                                            option.note ? (
                                                <span
                                                    style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
                                                >
                                                    {option.label}
                                                    <Tooltip content={option.note}>
                                                        <Icon source={InfoIcon} tone="subdued" />
                                                    </Tooltip>
                                                </span>
                                            ) : (
                                                option.label
                                            )
                                        }
                                        helpText={option.helpText}
                                        id={`discounted_variant_mode_${option.value}`}
                                        name="discounted_variant_mode"
                                        {...asChoiceField(discounted_variant_mode, option.value)}
                                        onChange={() => {
                                            dispatchBundleConfig({
                                                payload: option.value,
                                                key: "discounted_variant_mode",
                                            });
                                            discounted_variant_mode.onChange(option.value);
                                        }}
                                    />
                                ))}
                            </div>
                            {discounted_variant_mode.value === "same_as_buy" &&
                                !!discounted_products.value?.length &&
                                !discounted_products.value.some((dp: ProductObj) =>
                                    products.value.some((p: ProductObj) => p.id === dp.id),
                                ) && (
                                    <div style={{ marginTop: "6px" }}>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            None of the discounted products are in the Buy list, so customers will
                                            choose variants for all of them.
                                        </Text>
                                    </div>
                                )}
                        </div>
                    </FormLayout>
                </Collapsible>

                <ComboProductPage
                    dispatchBundleConfig={dispatchBundleConfig}
                    isProductPageVisible={isProductPageVisible}
                    submit={submit}
                    product_description={product_description}
                    product_title={product_title}
                    images={images}
                    collections={collections}
                    double_discount={double_discount}
                    bundle_type={"bxgy"}
                    products={useField([...products?.value, ...discounted_products?.value])}
                    default_cta={default_cta}
                />

                <Discount
                    dispatchBundleConfig={dispatchBundleConfig}
                    discount_type={discount_type}
                    discount_value={discount_value}
                    order_discounts={order_discounts}
                    product_discounts={product_discounts}
                    shipping_discounts={shipping_discounts}
                    isMobile={isMobile}
                    bundleType={BundleTypes.BXGY}
                    products={discounted_products}
                    discount_label_template={discount_label_template}
                    discount_label_template_helptext={""}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <FreeGift
                    dispatchBundleConfig={dispatchBundleConfig}
                    allow_free_gifts={allow_free_gifts}
                    free_gifts={free_gifts}
                    free_gift_text={free_gift_text}
                    submit={submit}
                    excludeProducts={[...products.value, ...discounted_products.value]}
                />

                <Checkbox
                    label={"Show Product Quantity"}
                    value=""
                    checked={show_product_quantity.value}
                    onChange={() => {
                        const newVal = !show_product_quantity.value;
                        dispatchBundleConfig({
                            payload: newVal,
                            key: "show_product_quantity",
                        });
                        show_product_quantity.onChange(newVal);
                    }}
                />

                {/* 
                        {isProductPageVisible.value && (
                            <IncludedProductPage
                                product_handle={product_handle}
                                show_on_product_page={show_on_product_page}
                                products={combinedProducts}
                                dispatchbundleConfig={dispatchBundleConfig}
                                bundleContext={BxgyBundleContext}
                            />
                        )} */}
                {((products.value?.length ?? 0) > 0 || (discounted_products.value?.length ?? 0) > 0) && (
                    <>
                        <div className={styles.dividerStyle}>
                            <Divider borderColor="border" />
                        </div>

                        <AllowedProductPages
                            validProductPages={allowed_product_pages}
                            dispatchBundleConfig={dispatchBundleConfig}
                            products={[...products.value, ...discounted_products.value]}
                            dependencyArr={[products, discounted_products]}
                            updateKey={"allowed_product_pages"}
                            render_product_pages_type={render_product_pages_type}
                            bundleType={BundleTypes.BXGY}
                        />
                    </>
                )}

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <SwatchesSelectorWrapper
                    dispatchBundleConfig={dispatchBundleConfig}
                    show_swatches={show_swatches}
                    swatchOptions={swatchOptions}
                    swatchesProductsMap={swatchesProductsMap}
                    swatchShape={swatchShape}
                    swatchSize={swatchSize}
                    productSource={{
                        type: "specific",
                        value: [...(products?.value || []), ...(discounted_products?.value || [])],
                    }}
                    bundleType={BundleTypes.BXGY}
                    bundleConfig={bundleConfig}
                    SwatchesBundleConfigTouchedRef={SwatchesBundleConfigTouchedRef}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <TextFields
                    dispatchBundleConfig={dispatchBundleConfig}
                    bundleType={BundleTypes.BXGY}
                    add_to_cart_cta_text={add_to_cart_cta_text}
                    default_cta={default_cta}
                    quantity_text={quantity_text}
                    footer_total_text={footer_total_text}
                    y_label_get_text={y_label_get_text}
                    free_text={free_text}
                    y_label_off_text={y_label_off_text}
                    x_label_buy_text={x_label_buy_text}
                />
            </div>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            <div className={styles.bottomRowCta}>
                <span></span>
                <Button variant="secondary" onClick={() => handleNext(1, "Design")}>
                    Next
                </Button>
            </div>
        </div>
    );
};

export default BxgyContentForm;
